import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Crown, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export default function Success() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchSubscription, subscription } = useSubscription();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isManualCheck, setIsManualCheck] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);

  const sessionId = searchParams.get('session_id');

  // Konfetti-Effekt nur einmal beim Laden
  useEffect(() => {
    const triggerConfetti = () => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10B981', '#059669', '#34D399', '#6EE7B7'],
      });
      
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: ['#10B981', '#059669', '#34D399', '#6EE7B7'],
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: ['#10B981', '#059669', '#34D399', '#6EE7B7'],
        });
      }, 250);
    };

    triggerConfetti();
  }, []); // Nur einmal ausführen

  // Memoized verification function
  const verifyPayment = useCallback(async () => {
    if (verificationComplete) return; // Verhindere mehrfache Ausführung
    
    if (!sessionId) {
      setError('Keine Session-ID gefunden');
      setIsVerifying(false);
      setVerificationComplete(true);
      return;
    }

    if (!user) {
      // Warte kurz auf User-Authentifizierung, falls sie noch lädt
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (!user) {
        setError('Benutzer nicht angemeldet');
        setIsVerifying(false);
        setVerificationComplete(true);
        return;
      }
    }

    try {
      console.log('🔍 Verifying payment for session:', sessionId);
      
      // Verhindere mehrfache Toast-Nachrichten
      let toastShown = false;
      
      // Mehrere Verifikationsversuche mit zunehmenden Wartezeiten
      const maxAttempts = 8; // Reduziert für bessere Performance
      let attempt = 1;
      let success = false;

      while (attempt <= maxAttempts && !success && !verificationComplete) {
        console.log(`🔄 Verification attempt ${attempt}/${maxAttempts}`);
        
        // Hole aktuellste Subscription-Daten (ohne Dependencies-Problem)
        try {
          await fetchSubscription();
          
          // Prüfe ob Premium aktiviert wurde
          if (subscription?.status === 'active' && subscription?.tier === 'yearly') {
            console.log('✅ Premium subscription found and active');
            success = true;
            break;
          }
        } catch (fetchError) {
          console.log('Context fetch failed, trying direct DB query:', fetchError);
        }

        // Prüfe auch direkt in der Datenbank nach neuesten Updates
        try {
          const { data: recentSubscription, error: subError } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .eq('tier', 'yearly')
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          if (!subError && recentSubscription) {
            console.log('✅ Found active subscription via direct DB check');
            success = true;
            // Sync Context ohne Dependencies-Problem
            try {
              await fetchSubscription();
            } catch (e) {
              console.log('Context sync failed, but subscription found:', e);
            }
            break;
          }
        } catch (dbError) {
          console.log('Direct DB check failed, continuing with webhook wait:', dbError);
        }

        if (attempt < maxAttempts && !verificationComplete) {
          // Progressive Wartezeiten: 2s, 3s, 4s, 5s, 6s, 7s, 8s
          const waitTime = 1000 + (attempt * 1000);
          console.log(`⏳ Waiting ${waitTime/1000}s before next attempt...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
        
        attempt++;
      }

      if (success && !toastShown) {
        setVerificationSuccess(true);
        toast.success('🎉 Zahlung erfolgreich verarbeitet!');
        toastShown = true;
      } else if (!success) {
        console.warn('⚠️ Payment verification timed out, but payment may still be processed');
        setError(
          'Die Zahlung wird noch verarbeitet. Dies kann bis zu 5 Minuten dauern. Falls dein Premium-Account nicht automatisch aktiviert wird, verwende den "Zahlung erneut prüfen" Button.'
        );
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setError('Fehler beim Überprüfen der Zahlung. Falls du bezahlt hast, wird dein Account in wenigen Minuten aktiviert.');
    } finally {
      setIsVerifying(false);
      setVerificationComplete(true);
    }
  }, [sessionId, user, subscription, verificationComplete]); // Entfernt fetchSubscription aus Dependencies

  // Einmalige Verifizierung ohne Endlos-Schleife
  useEffect(() => {
    if (!verificationComplete) {
      verifyPayment();
    }
  }, [verifyPayment]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleManualCheck = useCallback(async () => {
    if (isManualCheck) return; // Verhindere mehrfache Ausführung
    
    setIsManualCheck(true);
    setError(null);
    
    try {
      console.log('🔍 Manual payment verification started');
      
      if (!user) {
        setError('Benutzer nicht angemeldet. Bitte lade die Seite neu.');
        return;
      }
      
      // Mehrere Verifikationsstrategien
      let success = false;
      
      // 1. Direkte Datenbankabfrage (zuerst, da zuverlässiger)
      const { data: directSubscription, error: directError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .eq('tier', 'yearly')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!directError && directSubscription) {
        console.log('✅ Active subscription found via direct DB query');
        success = true;
        // Sync Context
        try {
          await fetchSubscription();
        } catch (e) {
          console.log('Context sync failed, but subscription found:', e);
        }
      }
      
      // 2. Prüfe auch nach Session-ID in webhook_logs
      if (!success && sessionId) {
        const { data: webhookLog, error: logError } = await supabase
          .from('webhook_logs')
          .select('*')
          .eq('payment_id', sessionId)
          .eq('success', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (!logError && webhookLog) {
          console.log('✅ Successful payment found in webhook logs');
          // Warte kurz und aktualisiere erneut
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Prüfe erneut nach Subscription
          const { data: retrySubscription } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .eq('tier', 'yearly')
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          if (retrySubscription) {
            success = true;
            try {
              await fetchSubscription();
            } catch (e) {
              console.log('Context sync failed, but subscription found:', e);
            }
          }
        }
      }
      
      if (success) {
        setVerificationSuccess(true);
        setError(null);
        toast.success('🎉 Premium-Account erfolgreich aktiviert!');
      } else {
        setError('Premium-Account noch nicht aktiviert. Die Verarbeitung kann bis zu 5 Minuten dauern. Falls das Problem weiterhin besteht, kontaktiere bitte den Support.');
      }
    } catch (error) {
      console.error('Manual verification error:', error);
      setError('Fehler bei der manuellen Prüfung. Bitte versuche es in wenigen Minuten erneut.');
    } finally {
      setIsManualCheck(false);
    }
  }, [user, sessionId, isManualCheck, fetchSubscription]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
            {isVerifying ? (
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            ) : verificationSuccess ? (
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            {isVerifying ? 'Zahlung wird verarbeitet...' : 
             verificationSuccess ? 'Zahlung erfolgreich!' : 
             'Fehler bei der Zahlung'}
          </CardTitle>
        </CardHeader>

        <CardContent className="text-center space-y-6">
          {isVerifying && (
            <div className="space-y-3">
              <p className="text-gray-600">
                Deine Zahlung wird gerade verarbeitet. Dies kann einen Moment dauern.
              </p>
              <div className="text-sm text-gray-500">
                Bitte schließe diese Seite nicht.
              </div>
            </div>
          )}

          {verificationSuccess && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-emerald-600 font-medium">
                <Crown className="w-5 h-5" />
                <span>Willkommen bei KaufPause Premium!</span>
              </div>
              
              <p className="text-gray-600">
                Deine Zahlung war erfolgreich und dein Premium-Account wurde aktiviert. 
                Du hast jetzt Zugang zu allen Premium-Features!
              </p>

              {subscription?.tier === 'yearly' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-800">
                    <strong>Jahres-Lizenz aktiviert</strong> - Gültig bis{' '}
                    {subscription.premium_end_date && 
                      new Date(subscription.premium_end_date).toLocaleDateString('de-DE')
                    }
                  </p>
                </div>
              )}

              <Button 
                onClick={handleGoToDashboard}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Zum Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <p className="text-red-600">{error}</p>
              <div className="space-y-2">
                <Button 
                  onClick={handleManualCheck}
                  disabled={isManualCheck}
                  variant="outline"
                  className="w-full"
                >
                  {isManualCheck ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Zahlung wird geprüft...
                    </>
                  ) : (
                    'Zahlung erneut prüfen'
                  )}
                </Button>
                <Button 
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="w-full"
                >
                  Seite neu laden
                </Button>
                <Button 
                  onClick={handleGoToDashboard}
                  className="w-full"
                >
                  Zum Dashboard
                </Button>
              </div>
            </div>
          )}

          {sessionId && (
            <div className="text-xs text-gray-400 mt-4">
              Session-ID: {sessionId.substring(0, 20)}...
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 